import { mapLabResults } from '../api/mappers/labResultMapper';
import { mapImmunizationResults } from '../api/mappers/immunizationResultMapper';
import * as vsApi from '../api/vsApi'
import { handleErrors, ERROR_CONTEXT_RESULTS } from '../util/error_v3'
import ExtendedError from '../util/extendedError'
import { saveAs } from 'file-saver';
import moment from 'moment'
import { LANG_EN, LANG_FR, getLanguageStrings } from '../util/language_v3';
import { intlFormatDate, PDF_FILE_NAME_FORMAT } from '../util/util';

export const fetchLabResults = () => {
    return {
        type: "FETCH_LAB_RESULTS",
    }
}

export const fetchImmunizationResults = () => {
    return {
        type: "FETCH_IMMUNIZATION_RESULTS",
    }
}

export const fetchDeviceType = () => {
    return {
        type: "FETCH_DEVICE_TYPE",
    }
}

export const receiveLabResults = (data) => {
    return {
        type: "RECEIVE_LAB_RESULTS",
        data: data,
    }
}

export const receiveImmunizationResults = (data) => {
    return {
        type: "RECEIVE_IMMUNIZATION_RESULTS",
        data: data,
    }
}

export const setUser = (data) => {
    return {
        type: "SET_USER",
        data: data,
    }
}

export const setSelectedLabResult = (data) => {
    return {
        type: "SET_SELECTED_LAB_RESULT",
        data: data,
    }
}

export const errorFetching = (data) => {
    return {
        type: "FETCH_ERROR",
        error: data,
    }
}

export const setErrorState = (state, err, fetch) => {
    return {
        type: "SET_ERROR_STATE",
        state: state,
        error: err,
        fetch: fetch,
    }
}

export const clearData = () => {
    return {
        type: "CLEAR_DATA",
    }
}

export const createDeviceId = () => {
    return {
        type: "CREATE_DEVICE_ID",
    }
}

export const fetchResultsPdf = (data) => {
    return {
        type: "FETCH_RESULTS_PDF",
        data: data,
    }
}

export const fetchDemographics = (data) => {
    return {
        type: "FETCH_DEMOGRAPHICS",
        data: data,
    }
}

export const receiveResultsPdf = (data) => {
    return {
        type: "RECEIVE_RESULTS_PDF",
        data: data,
    }
}

export const receiveDemographics = (data) => {
    return {
        type: "RECEIVE_DEMOGRAPHICS",
        data: data,
    }
}

export const fetchDemographicsThunk = () => {
    return function (dispatch, getState) {
        let fetching = getState().api.isLoadingLabResults;
        let deviceId = getState().api.deviceId;
        // Now send this to vs auth api
        if (fetching !== true) {
            dispatch(fetchDemographics(true));
            let language = getState().language.language;
            let authorizationToken = getState().auth.authorizationToken;
            // Fetch the data...
            return vsApi.getDemographics(language, deviceId, authorizationToken)
                .then(response => {
                    return response.json().then(results => ({ results: results, statusCode: response.status }));
                })
                .then((obj) => {
                    let results = obj.results;
                    let statusCode = obj.statusCode;
                    let error = vsApi.handleGetTestResultsErrors(statusCode, results);
                    if (error.error === true) {
                        // TODO: Make new context?
                        handleErrors(error, language, ERROR_CONTEXT_RESULTS);
                    }
                    return results;
                })
                .then(results => {
                    // Map whatever comes from the api into local model objects
                    if (results.data !== null) {
                        dispatch(receiveDemographics(false));
                        dispatch(setUser(results.data));
                    }
                })
                .catch(err => {
                    throw new ExtendedError(err.message, err.code, err.errorObj);
                });
        }
    }
}

export const fetchLabResultsThunk = () => {
    return function (dispatch, getState) {
        let fetching = getState().api.isLoadingLabResults;
        let deviceId = getState().api.deviceId;
        // Now send this to vs auth api
        if (fetching !== true) {
            dispatch(fetchLabResults());
            let language = getState().language.language;
            let authorizationToken = getState().auth.authorizationToken;
            // Fetch the data...
            return vsApi.getTestResults(language, deviceId, authorizationToken)
                .then(response => {
                    return response.json().then(results => ({ results: results, statusCode: response.status }));
                })
                .then((obj) => {
                    let results = obj.results;
                    let statusCode = obj.statusCode;
                    let error = vsApi.handleGetTestResultsErrors(statusCode, results);
                    if (error.error === true) {
                        handleErrors(error, language, ERROR_CONTEXT_RESULTS);
                    }
                    return results;
                })
                .then(results => {
                    // Map whatever comes from the api into local model objects
                    if (results.data !== null && results.data.patientResults != null) {
                        let { user, sortedResults } = mapLabResults(results.data.patientResults);
                        dispatch(receiveLabResults(sortedResults));
                        dispatch(setUser(user));
                    }
                })
                .catch(err => {
                    throw new ExtendedError(err.message, err.code, err.errorObj);
                });
        }
    }
}

export const fetchResultsPdfThunk = () => {
    return function (dispatch, getState) {
        const fetchingPdf = getState().api.isLoadingLabResultsPdf;
        const deviceId = getState().api.deviceId;
        const selectedResult = getState().api.selectedLabResult;
        const language = getState().language.language;
        const strings = getLanguageStrings(language);
        if (fetchingPdf !== true) {
            dispatch(fetchResultsPdf(true));
            let headers;
            let status;
            let authorizationToken = getState().auth.authorizationToken;
            return vsApi.getResultsPdf(language, deviceId, authorizationToken, selectedResult.resultId)
                .then(response => {
                    status = response.status;
                    headers = response.headers;
                    return parseJson(response, language);
                })
                .then(async response => {
                    let contentType = headers.get("content-type");
                    let length = headers.get("content-length");
                    let json = response.results;
                    if (status === 200 && length !== 0 && contentType !== "application/json;charset=UTF-8") {
                        let blob = await response.blob();
                        const fileName = intlFormatDate(selectedResult.resultDate, PDF_FILE_NAME_FORMAT, language, '-') + ' SH-CORD ' + strings.PDF_FILE_NAME;
                        // Omega tier hack to get download to work automatically across mobile/desktop
                        // 1. Create blob link to download
                        const url = window.URL.createObjectURL(new Blob([blob]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileName);
                        // 2. Append to html page
                        document.body.appendChild(link);
                        // 3. Force download
                        link.click();
                        // 4. Clean up and remove the link
                        link.parentNode.removeChild(link);
                        dispatch(receiveResultsPdf(false));
                    } else {
                        let error = vsApi.handleGetTestResultsErrors(status, json, length, contentType, strings);
                        if (error.error === true) {
                            handleErrors(error, language, ERROR_CONTEXT_RESULTS);
                        }
                    }
                })
                .catch(err => {
                    dispatch(receiveResultsPdf(false))
                    throw new ExtendedError(err.message, err.code, err.errorObj);
                });
        }
    }
}

export const fetchImmunizationResultsThunk = () => {
    return function (dispatch, getState) {
        let fetching = getState().api.isLoadingImmunizationResults;
        let deviceId = getState().api.deviceId;
        // Now send this to vs auth api
        if (fetching !== true) {
            dispatch(fetchImmunizationResults());
            let language = getState().language.language;
            let authorizationToken = getState().auth.authorizationToken;
            // Fetch the data...
            return vsApi.getImmunizationResults(language, deviceId, authorizationToken)
                .then(response => {
                    return response.json().then(results => ({ results: results, statusCode: response.status }));
                })
                .then((obj) => {
                    let results = obj.results;
                    let statusCode = obj.statusCode;
                    // FIXME: rename this to a generic error handler
                    let error = vsApi.handleGetTestResultsErrors(statusCode, results);
                    if (error.error === true) {
                        handleErrors(error, language, ERROR_CONTEXT_RESULTS);
                    }
                    return results;
                })
                .then(results => {
                    // Map whatever comes from the api into local model objects
                    if (results.data !== null && results.data.patientImmunization != null) {
                        console.warn(results.data)
                        let { user, sortedResults } = mapImmunizationResults(results.data.patientImmunization);
                        dispatch(receiveImmunizationResults(sortedResults));
                        dispatch(setUser(user));
                    }
                })
                .catch(err => {
                    throw new ExtendedError(err.message, err.code, err.errorObj);
                });
        }
    }
}

export const parseJson = (response, language) => {
    const contentType = response.headers.get("content-type");
    const length = response.headers.get("content-length");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json().then(results => ({ results: results, statusCode: response.status }));
    }
    else if (length !== 0 && contentType && contentType.indexOf("application/pdf") !== -1) {
        return response;
    }
    else {
        return response.text().then(text => {
            let strings = getLanguageStrings(language);
            console.log("status code: " + response.status + "\nresponse: " + text)
            throw new Error(strings.BASIC_ERROR + " " + response.status);
        })
    }
}